/* ============================================
   Native Possum — np-wishlist.js
   Wishlist page. The list lives in the wishlist app; this file only
   renders it in NP's own look, through the app embed's public API
   (window.wishlist) and nothing else:
     own list:    items(), remove(productId), shareUrl()
     ?share=<t>:  sharedItems(t) — read-only view of someone's list
   Saves are per PRODUCT; colour/size is picked at add-to-bag time
   (desktop select / mobile size sheet). Product data comes from
   /products/{handle}.js. Add to bag: window.npCart.add (np-cart.js).
   Mobile size sheet: an npLayer (np-utilities.js) — scroll lock, focus, Escape.
   Used by: templates/page.wishlist.liquid
   ============================================ */
(function () {
  'use strict';

  var SHARE_PARAM = 'share';
  var announcer;
  var MOBILE_BREAKPOINT = 768;

  /* ---- Size sheet state ---- */
  var sizeSheet = null;
  var sheetOverlay = null;
  var pendingItem = null;
  var pendingSelectedSize = '';
  var sheetTrigger = null;

  /* ---- Wishlist app ---- */

  // Calls fn(api) once the embed has loaded its state (so a guest list it
  // just merged into an account is already where items() will look), or
  // fn(null) if the embed is not on the page.
  function whenWishlistReady(fn) {
    var done = false;
    function go() {
      if (done) return;
      done = true;
      fn(window.wishlist || null);
    }
    if (window.wishlist && window.wishlist.state && window.wishlist.state.ready) return go();
    document.addEventListener('wishlist:ready', go);
    document.addEventListener('wishlist:error', function (e) {
      if (e.detail && e.detail.action === 'load') go();
    });
    window.addEventListener('load', function () { if (!window.wishlist) go(); });
  }

  function sharedToken() {
    try {
      return new URLSearchParams(window.location.search).get(SHARE_PARAM) || '';
    } catch (e) {
      return '';
    }
  }

  /* ---- Toast notification ---- */

  // quiet: visual only — used when npCart has already announced the message.
  // isError: stays until the shopper acts; otherwise a reading time (npMessage).
  function showToast(message, quiet, isError) {
    var toast = document.getElementById('npWishlistToast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'npWishlistToast';
      toast.className = 'np-wishlist-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    if (quiet) toast.setAttribute('aria-hidden', 'true');
    else toast.removeAttribute('aria-hidden');
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.npMessage[isError ? 'hold' : 'dwell'](toast, function () {
      toast.classList.remove('is-visible');
    });
  }

  /* ---- Screen reader announcer ---- */

  function announce(message) {
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.className = 'np-visually-hidden';
      document.body.appendChild(announcer);
    }
    announcer.textContent = '';
    setTimeout(function () {
      announcer.textContent = message;
    }, 100);
  }

  /* ---- Mobile check ---- */

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  /* ---- Render: Share Buttons ---- */

  function renderShareRow(container, shareUrl) {
    var row = document.createElement('div');
    row.className = 'np-wishlist-share';

    var label = document.createElement('span');
    label.className = 'np-wishlist-share-label';
    label.textContent = 'Share your wishlist';
    row.appendChild(label);

    // Facebook
    var fbBtn = createShareBtn(
      'Share on Facebook',
      '<svg viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>',
      function () {
        window.open(
          'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(shareUrl),
          '_blank',
          'width=600,height=400'
        );
      }
    );
    row.appendChild(fbBtn);

    // WhatsApp
    var waBtn = createShareBtn(
      'Share on WhatsApp',
      '<svg viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>',
      function () {
        window.open(
          'https://wa.me/?text=' + encodeURIComponent('My Native Possum wishlist ' + shareUrl),
          '_blank'
        );
      }
    );
    row.appendChild(waBtn);

    // Messages (SMS/iMessage)
    var smsBtn = createShareBtn(
      'Share via Messages',
      '<svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
      function () {
        var body = encodeURIComponent('My Native Possum wishlist ' + shareUrl);
        // v8: iOS uses sms:&body=, Android uses sms:?body=
        var isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        var separator = isIOS ? '&' : '?';
        window.location.href = 'sms:' + separator + 'body=' + body;
      }
    );
    smsBtn.classList.add('np-wishlist-share-btn--sms');
    row.appendChild(smsBtn);

    // Copy Link
    var copyBtn = createShareBtn(
      'Copy wishlist link',
      '<svg viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>',
      function () {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(shareUrl).then(function () {
            showCopyTooltip(copyBtn);
            showToast('Link copied to clipboard');
          });
        } else {
          var textarea = document.createElement('textarea');
          textarea.value = shareUrl;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
          showCopyTooltip(copyBtn);
          showToast('Link copied to clipboard');
        }
      }
    );
    copyBtn.classList.add('np-wishlist-share-btn--copy');

    var tooltip = document.createElement('span');
    tooltip.className = 'np-wishlist-copy-tooltip';
    tooltip.textContent = 'Copied!';
    tooltip.setAttribute('aria-hidden', 'true');
    copyBtn.appendChild(tooltip);

    row.appendChild(copyBtn);

    container.parentNode.insertBefore(row, container);
  }

  function createShareBtn(label, svgHTML, onClick) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'np-wishlist-share-btn';
    btn.setAttribute('aria-label', label);
    btn.innerHTML = svgHTML;
    btn.addEventListener('click', onClick);
    return btn;
  }

  function showCopyTooltip(btn) {
    var tooltip = btn.querySelector('.np-wishlist-copy-tooltip');
    if (!tooltip) return;
    tooltip.classList.add('is-visible');
    window.npMessage.dwell(tooltip, function () { tooltip.classList.remove('is-visible'); });
  }

  /* ---- Render: Shared Banner ---- */

  function renderSharedBanner(container) {
    var banner = document.createElement('div');
    banner.className = 'np-wishlist-shared-banner';
    banner.innerHTML = '<p>These are items someone shared with you.<br>Tap <strong>Add to Bag</strong> to purchase, or browse our collections to build your own wishlist.</p>';
    container.parentNode.insertBefore(banner, container);
  }

  /* ---- Render: Empty State ---- */

  function renderEmptyState(container) {
    var mobile = isMobile();
    var actionWord = mobile ? 'tapping' : 'clicking';
    var html = '<div class="np-wishlist-empty">';
    html += '<div class="np-wishlist-empty-icon" aria-hidden="true"></div>';
    html += '<p class="np-wishlist-empty-text">Save your favourites by ' + actionWord + ' the heart icon on any product.</p>';
    html += '<div class="np-wishlist-empty-buttons">';
    html += '<a href="/collections/women" class="np-wishlist-empty-btn">Women</a>';
    html += '<a href="/collections/men" class="np-wishlist-empty-btn">Men</a>';
    html += '<a href="/collections/accessories" class="np-wishlist-empty-btn">Accessories</a>';
    html += '<a href="/collections/blankets-throws" class="np-wishlist-empty-btn">Blankets + Throws</a>';
    html += '</div>';
    html += '<a href="/collections/our-picks" class="np-wishlist-empty-btn-secondary np-wishlist-empty-btn--primary">Shop Our Picks</a>';
    html += '<p class="np-wishlist-empty-contact">Need help? <a href="/pages/contact">Get in touch</a></p>';
    html += '</div>';
    container.innerHTML = html;
  }

  /* ---- Render: Product Cards ---- */

  // App rows carry a product id and handle; everything shown comes from the
  // live product, so a renamed or repriced product shows what is true now.
  // A row whose product no longer exists is skipped.
  async function renderWishlistItems(container, rows, isSharedView) {
    var items = await Promise.all(
      rows.map(async function (row) {
        try {
          var response = await fetch('/products/' + encodeURIComponent(row.handle) + '.js');
          if (!response.ok) return null;
          var product = await response.json();
          var preselect = product.variants.find(function (v) { return v.available; }) || product.variants[0];
          return {
            productId: String(row.productId),
            variantId: String(preselect.id),
            productHandle: product.handle,
            productTitle: product.title,
            productImage: product.featured_image || '',
            productData: product
          };
        } catch (e) {
          return null;
        }
      })
    );

    items.forEach(function (item) {
      if (!item) return;
      var card = createProductCard(item, isSharedView);
      if (card) container.appendChild(card);
    });
    return items.filter(Boolean).length;
  }

  function createProductCard(item, isSharedView) {
    var card = document.createElement('div');
    card.className = 'np-wishlist-card';
    card.dataset.variantId = item.variantId;

    var product = item.productData;

    // Build variant options (desktop only, hidden on mobile via CSS)
    var variantOptions = '';
    if (product && product.variants) {
      product.variants.forEach(function (v) {
        var sel = v.id.toString() === item.variantId ? 'selected' : '';
        var txt = v.title !== 'Default Title' ? v.title : product.title;
        variantOptions += '<option value="' + v.id + '" ' + sel + '>' + escapeHTML(txt + pillPrice(product, v.price)) + '</option>';
      });
    }

    // The saved variant's own price (Bassinet and Cot differ); else "From" when prices vary
    var savedVariant = product && product.variants
      ? product.variants.filter(function (v) { return v.id.toString() === item.variantId; })[0]
      : null;
    var price = savedVariant
      ? window.npFormatMoney(savedVariant.price)
      : (product && product.price_varies ? 'From ' : '') + window.npFormatMoney(product ? product.price_min : 0);
    var img = item.productImage || (product ? product.featured_image : '');
    var title = item.productTitle || (product ? product.title : '');
    var brand = product ? product.vendor : '';

    var actionsHTML =
      '<div class="np-wishlist-card-actions">' +
      '<button class="np-wishlist-card-add" type="button">Add to Bag</button>' +
      (isSharedView ? '' : '<button class="np-wishlist-card-remove" type="button">Remove</button>') +
      '</div>';

    card.innerHTML =
      '<a href="/products/' + item.productHandle + '" class="np-wishlist-card-image" tabindex="-1" aria-hidden="true">' +
      '<img src="' + img + '" alt="" loading="lazy">' +
      '</a>' +
      (brand ? '<span class="np-wishlist-card-brand">' + escapeHTML(brand) + '</span>' : '') +
      '<a href="/products/' + item.productHandle + '" class="np-wishlist-card-title">' + escapeHTML(title) + '</a>' +
      (variantOptions ? '<select class="np-wishlist-card-select" aria-label="Select variant for ' + escapeHTML(title) + '">' + variantOptions + '</select>' : '') +
      '<div class="np-wishlist-card-price">' + price + '</div>' +
      actionsHTML;

    // Variant select change — swap card image to match colour
    var selectEl = card.querySelector('.np-wishlist-card-select');
    if (selectEl && product && product.variants) {
      selectEl.addEventListener('change', function () {
        var selectedVariant = product.variants.find(function (v) {
          return v.id.toString() === selectEl.value;
        });
        var priceEl = card.querySelector('.np-wishlist-card-price');
        if (selectedVariant && priceEl) priceEl.textContent = window.npFormatMoney(selectedVariant.price);
        if (selectedVariant && selectedVariant.featured_image) {
          var cardImg = card.querySelector('.np-wishlist-card-image img');
          if (cardImg) {
            cardImg.src = selectedVariant.featured_image.src;
          }
        }
      });
    }

    // Add to bag handler
    card.querySelector('.np-wishlist-card-add').addEventListener('click', function () {
      handleAddToBag(item, selectEl, this);
    });

    var removeBtn = card.querySelector('.np-wishlist-card-remove');
    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        removeItem(item, card, this);
      });
    }

    return card;
  }

  function escapeHTML(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
  }

  /* ---- Actions ---- */

  function handleAddToBag(item, selectEl, btn) {
    var product = item.productData;

    // Check if this product has multiple variants (i.e. sizes)
    var hasMultipleVariants = product && product.variants && product.variants.length > 1;
    var hasNonDefaultVariants = hasMultipleVariants && product.variants.some(function (v) {
      return v.title !== 'Default Title';
    });

    if (isMobile() && hasNonDefaultVariants) {
      // Mobile with sizes — open the size sheet
      openSizeSheet(item);
    } else {
      // Desktop (select dropdown visible) or single-variant product
      addToCart(item, selectEl, btn);
    }
  }

  // npCart adds (one queue, busy button), opens the bag drawer on success
  // and announces the result (into the bag's live region once it's open).
  // The open bag is the confirmation, as on the product page — no toast.
  // A failure gets a toast (visual only; npCart has already announced it).
  function addToCart(item, selectEl, btn) {
    var id = selectEl ? selectEl.value : item.variantId;
    return window.npCart.add([{ id: id, quantity: 1 }], { control: btn })
      .catch(function (e) {
        showToast(e.message || 'Something went wrong. Please try again.', true, true);
      });
  }

  // remove() clears the product (every variant) through the embed, so its
  // state, the hearts and the header mark stay in step. Resolves true once
  // the product is off the list, false if the removal failed.
  function removeItem(item, card, btn) {
    if (!window.wishlist) {
      showToast('Something went wrong. Please try again.', false, true);
      return;
    }
    btn.disabled = true;
    window.wishlist.remove(item.productId).then(function (removed) {
      if (!removed) {
        btn.disabled = false;
        showToast('Something went wrong. Please try again.', false, true);
        return;
      }
      afterRemove(card, item.productTitle);
    });
  }

  function afterRemove(card, productTitle) {
    card.classList.add('is-removing');
    announce(productTitle + ' removed from wishlist');
    showToast(productTitle + ' removed from wishlist');

    setTimeout(function () {
      card.remove();
      var container = document.querySelector('.np-wishlist-grid');
      var remainingCards = container.querySelectorAll('.np-wishlist-card');
      if (remainingCards.length === 0) {
        var shareRow = document.querySelector('.np-wishlist-share');
        if (shareRow) shareRow.remove();
        announce('Your wishlist is now empty');
        renderEmptyState(container);
      }
    }, 500);
  }

  /* ============================================
     v3: MOBILE SIZE SHEET
     ============================================ */

  function buildSizeSheetShell() {
    // Overlay
    sheetOverlay = document.createElement('div');
    sheetOverlay.className = 'np-wl-sheet-overlay';
    sheetOverlay.setAttribute('aria-hidden', 'true');
    document.body.appendChild(sheetOverlay);

    // Sheet
    sizeSheet = document.createElement('div');
    sizeSheet.className = 'np-wl-size-sheet';
    sizeSheet.setAttribute('role', 'dialog');
    sizeSheet.setAttribute('aria-label', 'Select size');
    sizeSheet.setAttribute('aria-modal', 'true');
    sizeSheet.inert = true;

    sizeSheet.innerHTML =
      '<div class="np-wl-sheet-handle"></div>' +
      '<div class="np-wl-sheet-header">' +
        '<span class="np-wl-sheet-title">Select Size</span>' +
        '<button class="np-wl-sheet-close" type="button" aria-label="Close size selector">\u00d7</button>' +
      '</div>' +
      '<div class="np-wl-sheet-body">' +
        '<div class="np-wl-size-pills" role="listbox" aria-label="Available sizes"></div>' +
      '</div>' +
      '<div class="np-wl-sheet-footer">' +
        '<button class="np-wl-sheet-add" type="button" disabled>Add to Bag</button>' +
      '</div>';

    sizeSheet.querySelector('.np-wl-sheet-close').addEventListener('click', closeSizeSheet);
    sizeSheet.querySelector('.np-wl-sheet-add').addEventListener('click', function () {
      if (pendingItem && pendingSelectedSize) {
        addToCartWithSize(pendingItem, pendingSelectedSize, this, sheetTrigger);
        closeSizeSheet();
      }
    });

    document.body.appendChild(sizeSheet);
  }

  function openSizeSheet(item) {
    if (!sizeSheet) buildSizeSheetShell();

    var product = item.productData;
    if (!product) return;

    pendingItem = item;
    pendingSelectedSize = '';
    // v9: Remember what opened the sheet so we can restore focus on close
    sheetTrigger = document.activeElement;

    // Set product name in header
    var titleEl = sizeSheet.querySelector('.np-wl-sheet-title');
    titleEl.textContent = item.productTitle;

    // Populate size pills — grouped by colour or flat
    var pillsContainer = sizeSheet.querySelector('.np-wl-size-pills');
    pillsContainer.innerHTML = '';

    var hasMultipleOptions = product.options && product.options.length > 1;

    if (hasMultipleOptions) {
      // Multi-option product (Colour / Size) — group by colour
      var colourGroups = {};
      var colourOrder = [];

      product.variants.forEach(function (v) {
        if (v.title === 'Default Title') return;
        // Variant titles are "Colour / Size"
        var parts = v.title.split(' / ');
        var colour = parts[0] || v.title;
        var size = parts[1] || v.title;

        if (!colourGroups[colour]) {
          colourGroups[colour] = [];
          colourOrder.push(colour);
        }
        colourGroups[colour].push({
          id: v.id,
          size: size,
          fullTitle: v.title,
          available: v.available,
          price: v.price
        });
      });

      colourOrder.forEach(function (colour) {
        var heading = document.createElement('div');
        heading.className = 'np-wl-colour-heading';
        heading.textContent = colour;
        pillsContainer.appendChild(heading);

        var pillRow = document.createElement('div');
        pillRow.className = 'np-wl-pill-row';

        colourGroups[colour].forEach(function (variant) {
          var pill = document.createElement('button');
          pill.className = 'np-wl-size-pill';
          pill.type = 'button';
          pill.setAttribute('role', 'option');
          pill.setAttribute('aria-selected', 'false');
          pill.dataset.variantId = variant.id;
          pill.dataset.fullTitle = variant.fullTitle;
          pill.textContent = variant.size + pillPrice(product, variant.price);

          if (!variant.available) {
            pill.disabled = true;
            pill.classList.add('unavailable');
            pill.setAttribute('aria-disabled', 'true');
            pill.setAttribute('aria-label', variant.size + ' \u2014 sold out');
          }

          pill.addEventListener('click', function () {
            selectSheetSize(pill);
          });
          pillRow.appendChild(pill);
        });

        pillsContainer.appendChild(pillRow);
      });
    } else {
      // Single-option product (just sizes) — flat pills
      product.variants.forEach(function (v) {
        if (v.title === 'Default Title') return;
        var pill = document.createElement('button');
        pill.className = 'np-wl-size-pill';
        pill.type = 'button';
        pill.setAttribute('role', 'option');
        pill.setAttribute('aria-selected', 'false');
        pill.dataset.variantId = v.id;
        pill.dataset.fullTitle = v.title;
        pill.textContent = v.title + pillPrice(product, v.price);

        if (!v.available) {
          pill.disabled = true;
          pill.classList.add('unavailable');
          pill.setAttribute('aria-disabled', 'true');
          pill.setAttribute('aria-label', v.title + ' \u2014 sold out');
        }

        pill.addEventListener('click', function () {
          selectSheetSize(pill);
        });
        pillsContainer.appendChild(pill);
      });
    }

    // Reset footer button
    var footerBtn = sizeSheet.querySelector('.np-wl-sheet-add');
    footerBtn.disabled = true;
    footerBtn.textContent = 'Add to Bag';

    sheetOverlay.classList.add('active');
    sheetOverlay.setAttribute('aria-hidden', 'false');
    sizeSheet.classList.add('active');

    // Shared layer helper: scroll lock, inert page, focus in (first
    // available pill) and back to the opener, Escape.
    window.npLayer.open(sizeSheet, {
      trigger: sheetTrigger,
      backdrop: sheetOverlay,
      initialFocus: pillsContainer.querySelector('.np-wl-size-pill:not([disabled])'),
      lockScroll: true,
      onRequestClose: closeSizeSheet
    });
  }

  function closeSizeSheet() {
    if (!sizeSheet || !window.npLayer.isOpen(sizeSheet)) return;

    sizeSheet.classList.remove('active');
    sheetOverlay.classList.remove('active');
    sheetOverlay.setAttribute('aria-hidden', 'true');

    pendingItem = null;
    pendingSelectedSize = '';
    sheetTrigger = null;

    // Focus back to the opener (if it was still in the sheet) and scroll unlock
    window.npLayer.close(sizeSheet);
  }

  // ' · $185' on a size pill when the product's variants are priced differently
  function pillPrice(product, cents) {
    return product && product.price_varies ? ' \u00b7 ' + window.npFormatMoney(cents) : '';
  }

  function selectSheetSize(pill) {
    var variantId = pill.dataset.variantId;
    var displayText = pill.textContent;
    pendingSelectedSize = variantId;

    // Update pill active states — deselect all, select this one
    sizeSheet.querySelectorAll('.np-wl-size-pill').forEach(function (p) {
      var isActive = p.dataset.variantId === variantId;
      p.classList.toggle('active', isActive);
      p.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    // Update footer button
    var footerBtn = sizeSheet.querySelector('.np-wl-sheet-add');
    footerBtn.disabled = false;
    footerBtn.textContent = 'Add to Bag \u2013 ' + displayText;
  }

  // btn: the sheet's Add button (busy while pending). trigger: the card
  // button that opened the sheet — focus returns there when the bag closes.
  function addToCartWithSize(item, variantId, btn, trigger) {
    var product = item.productData;
    if (!product) return;

    // The open bag confirms the add (see addToCart); failures get a toast
    return window.npCart.add([{ id: variantId, quantity: 1 }], { control: btn, trigger: trigger || null })
      .catch(function (e) {
        showToast(e.message || 'Something went wrong. Please try again.', true, true);
      });
  }

  /* ---- Init ---- */

  // Not the empty state: an unreachable list must not read as an empty one.
  function renderLoadError(container) {
    container.innerHTML =
      '<div class="np-wishlist-empty">' +
      '<p class="np-wishlist-empty-text">We couldn’t load this wishlist just now. Please refresh the page to try again.</p>' +
      '</div>';
  }

  function renderEmptySharedState(container) {
    var html = '<div class="np-wishlist-empty">';
    html += '<div class="np-wishlist-empty-icon" aria-hidden="true"></div>';
    html += '<p class="np-wishlist-empty-text">This shared wishlist is no longer available. The link may have expired, or the items may have been removed from our store.</p>';
    html += '<div class="np-wishlist-empty-buttons">';
    html += '<a href="/collections/women" class="np-wishlist-empty-btn">Women</a>';
    html += '<a href="/collections/men" class="np-wishlist-empty-btn">Men</a>';
    html += '<a href="/collections/accessories" class="np-wishlist-empty-btn">Accessories</a>';
    html += '<a href="/collections/blankets-throws" class="np-wishlist-empty-btn">Blankets + Throws</a>';
    html += '</div>';
    html += '<a href="/collections/our-picks" class="np-wishlist-empty-btn-secondary np-wishlist-empty-btn--primary">Shop Our Picks</a>';
    html += '<p class="np-wishlist-empty-contact">Need help? <a href="/pages/contact">Get in touch</a></p>';
    html += '</div>';
    container.innerHTML = html;
  }

  function initShared(container, api, token) {
    var titleEl = document.querySelector('.np-wishlist-title');
    if (titleEl) titleEl.textContent = 'Shared Wishlist';
    if (!api) return renderLoadError(container);

    api.sharedItems(token)
      .then(function (data) {
        var rows = (data && data.items) || [];
        if (!rows.length) return renderEmptySharedState(container);
        renderSharedBanner(container);
        return renderWishlistItems(container, rows, true).then(function (shown) {
          if (shown) return;
          var banner = document.querySelector('.np-wishlist-shared-banner');
          if (banner) banner.remove();
          renderEmptySharedState(container);
        });
      })
      // sharedItems rejects with status 404 for an unknown or revoked token.
      .catch(function (error) {
        if (error && error.status === 404) renderEmptySharedState(container);
        else renderLoadError(container);
      });
  }

  function initOwn(container, api) {
    if (!api) return renderLoadError(container);

    api.items()
      .then(function (rows) {
        if (!rows.length) return renderEmptyState(container);
        return renderWishlistItems(container, rows, false).then(function (shown) {
          if (!shown) return renderEmptyState(container);
          // shareUrl() rejects when the shop has sharing off: no share row.
          // The link is minted once per list and reused, so links already
          // sent keep working.
          return api.shareUrl().then(function (url) {
            if (container.querySelector('.np-wishlist-card')) renderShareRow(container, url);
          }).catch(function () {});
        });
      })
      .catch(function () {
        renderLoadError(container);
      });
  }

  function init() {
    var container = document.querySelector('.np-wishlist-grid');
    if (!container) return;
    var token = sharedToken();
    whenWishlistReady(function (api) {
      if (token) initShared(container, api, token);
      else initOwn(container, api);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();